import React from 'react';
import { Reveal } from './ui/Reveal';
import { Mic } from 'lucide-react';
export interface Testimonial {
  quote: string;
  highlight: string;
  author: string;
  role: string;
  logo: string;
}
export const BRAND_TESTIMONIALS: Testimonial[] = [{
  quote: 'Zano made it much easier to source and compare talent across agencies. Having everything quickly accessible so we can easily present it to our client helped us move faster and close the deal.',
  highlight: 'easier to source',
  author: 'AB Lieberman',
  role: 'CEO, Clicks Talent Agency',
  logo: 'Clicks Talent'
}, {
  quote: 'Zano helped us easily connect with new agencies and creators which saved us so much valuable time!!',
  highlight: 'connect with new agencies',
  author: 'Liora Mizrachi',
  role: 'Influencer Marketing Manager, Lemon Adds',
  logo: 'Lemon Adds'
}, {
  quote: 'We love using Zano and finding it very valuable to partner with talent agencies',
  highlight: 'love',
  author: 'Valerie Cheney',
  role: 'Vice President, People First Marketing',
  logo: 'People First'
}, {
  quote: 'Zano made the entire process seamless.',
  highlight: 'seamless',
  author: 'Marie-Josée Elchab',
  role: 'Director of Influencer Marketing, AimeJay Media',
  logo: 'AimeJay Media'
}];
export const TALENT_TESTIMONIALS: Testimonial[] = [{
  quote: 'The platform is great so far - our agents LOVE the new public link feature and through this, we were able to share our full roster with links to a handful of our clients.',
  highlight: 'LOVE',
  author: 'Esther Na',
  role: 'Operations Lead, Viral Nation Talent',
  logo: 'Viral Nation'
}, {
  quote: 'We submitted a few of our creators to casting calls and been using it for our casting calls. Loving it so far :)',
  highlight: 'casting calls',
  author: 'Oliver Segall',
  role: 'Business Director, Connect Management',
  logo: 'Connect Management'
}, {
  quote: 'It definitely has been a really easy process to add our influencers and submit them to casting calls!',
  highlight: 'really easy',
  author: 'Lily Herridge-Baker',
  role: 'Director & Talent Manager, GSWL',
  logo: 'get social with lily'
}, {
  quote: 'I find the software and website easy to navigate, and the process of submitting my influencers for deals is straightforward.',
  highlight: 'straightforward',
  author: 'Dan Koretz',
  role: 'Business Development Manager, Bolen Media',
  logo: 'Bolen Media'
}];
const LOGO_IMAGES: Record<string, string> = {
  'Clicks Talent': 'https://cdn.magicpatterns.com/uploads/oKbracun8P5MXQXuDPFXrA/clicks_talent_logo.webp',
  'Lemon Adds': 'https://cdn.magicpatterns.com/uploads/h4wb5JCZ5wgSQRDatB7XF2/lemond_adds_logo.webp',
  'People First': 'https://cdn.magicpatterns.com/uploads/2LDCJdigeDPe9MhEqnZJ4w/people_first_logo.webp',
  'AimeJay Media': 'https://cdn.magicpatterns.com/uploads/wAhi58zjBoWzfSKYDDf2s2/aimejaymedia_loho.webp',
  'Viral Nation': 'https://cdn.magicpatterns.com/uploads/7L4QrP6BC72kQP1MKLHynK/viral_nation_logo.webp',
  'Connect Management': 'https://cdn.magicpatterns.com/uploads/qSZDJrnBPnaRxheghkryBG/connect_mgmt_logo.webp',
  'get social with lily': 'https://cdn.magicpatterns.com/uploads/9AGuTRTkwm263h954nUjHj/get_social_logo.webp',
  'Bolen Media': 'https://cdn.magicpatterns.com/uploads/1VXa1spdCh2YTZSuLPFCWs/bolen_media_logo.webp'
};
const LogoChip = ({
  name
}: {
  name: string;
}) => {
  if (LOGO_IMAGES[name]) {
    return <img src={LOGO_IMAGES[name]} alt={`${name} logo`} className="h-9 w-auto max-w-[130px] object-contain rounded-md" data-id="element-1744" />;
  }
  return <div className="text-xs font-bold text-gray-400" data-id="element-1745">{name}</div>;
};
const renderQuote = (quote: string, highlight: string) => {
  const parts = quote.split(new RegExp(`(${highlight})`, 'gi'));
  return <p className="text-gray-600 italic text-sm leading-relaxed" data-id="element-1746">
      "
      {parts.map((part, i) => part.toLowerCase() === highlight.toLowerCase() ? <span key={i} className="font-bold text-zano-pink not-italic" data-id="element-1747">
            {part}
          </span> : <span key={i} data-id="element-1748">{part}</span>)}
      "
    </p>;
};
interface TestimonialsProps {
  title: string;
  subtitle?: string;
  items: Testimonial[];
}
export const Testimonials = ({
  title,
  subtitle,
  items
}: TestimonialsProps) => {
  return <section className="py-20 bg-gray-50/50 border-y border-gray-100 overflow-hidden" data-id="element-1749">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1750">
        <div className="text-center mb-12" data-id="element-1751">
          <Reveal data-id="element-1752">
            <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-1753">{title}</h2>
            {subtitle && <p className="text-lg text-gray-600 max-w-2xl mx-auto" data-id="element-1754">
                {subtitle}
              </p>}
          </Reveal>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-1755">
          {items.map((item, i) => <Reveal key={i} delay={i * 0.1} className="h-full" data-id="element-1756">
              <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm hover:shadow-md transition-shadow h-full flex flex-col" data-id="element-1757">
                <div className="flex-1 mb-6" data-id="element-1758">
                  {renderQuote(item.quote, item.highlight)}
                </div>
                <div className="mt-auto" data-id="element-1759">
                  <p className="text-sm font-bold text-gray-900" data-id="element-1760">
                    {item.author}
                  </p>
                  <p className="text-xs text-gray-500 mb-4" data-id="element-1761">{item.role}</p>
                  <div className="pt-4 border-t border-gray-50 h-12 flex items-center" data-id="element-1762">
                    <LogoChip name={item.logo} data-id="element-1763" />
                  </div>
                </div>
              </div>
            </Reveal>)}
        </div>
      </div>
    </section>;
};